/* eslint-disable @altermaind/no-magic-numbers */
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getSurveyCampaignByIdResponsesResultsStatsCall } from '../../state';

export const ResultsByDepartments: FC = () => {
  const t = useTranslations('surveys.details.stats.tabDepartments.departments');
  const gapXs = useThemeValue('space', 'xs');
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();
  const { departmentsStatsWithMetrics } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { genericResponsiveValue } = useResponsiveStyle();

  const goToDetails = (id: string) => {
    navigate(Routes.DepartmentsDetail.replace(':departmentId', id));
  };

  return (
    <Box mt='l'>
      <Box
        mt='xl'
        mb='xs'
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='h2'>{t('title')}</Typography>
        {departmentsStatsWithMetrics && departmentsStatsWithMetrics.length > 3 && (
          <Box onClick={() => setShowMore(!showMore)} style={{ cursor: 'pointer' }}>
            {showMore ? (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography variant='p3/light'>{t('showLess')}</Typography>
                <Box style={{ transform: 'rotate(180deg)' }}>
                  <Svg name='down' size='s' fill='grey/light' />
                </Box>
              </Box>
            ) : (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography variant='p3/light'>{t('showMore')}</Typography>
                <Svg name='down' size='s' fill='grey/light' />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: { desktop: 'repeat(3, 1fr)' },
          defaultValue: 'repeat(1, 1fr)',
        })}
        style={{ gap: gapXs }}
        mb='l'
      >
        {(showMore
          ? departmentsStatsWithMetrics
          : departmentsStatsWithMetrics && departmentsStatsWithMetrics.slice(0, 3)
        )?.map(department => (
          <Card key={department.departmentId}>
            <Box
              alignItems='center'
              display='flex'
              flexDirection='row'
              justifyContent='space-between'
              mt='xxxs'
              pb='xs'
            >
              <Typography variant='h2'>{department.departmentName}</Typography>
            </Box>
            <Box display='flex' flexDirection='row' justifyContent='space-between' mt='xxs'>
              <Typography variant='p2/light'>
                {t('description', {
                  strength:
                    department.metricsScore &&
                    department.metricsScore.length > 0 &&
                    department.metricsScore
                      .filter(
                        (i): i is { score: number; metric?: { name?: string } } =>
                          typeof i.score === 'number' &&
                          !!i.metric &&
                          typeof i.metric.name === 'string',
                      )
                      .sort((a, b) => b.score - a.score)[0]
                      ?.metric?.name?.toLocaleLowerCase(),
                })}
              </Typography>
            </Box>
            <Button
              onClick={() => department && goToDetails(department?.departmentId || '')}
              variant='positive'
              mt='m'
              fontSize='s'
              fontWeight='500'
            >
              {t('viewDetails')}
            </Button>
          </Card>
        ))}
      </Box>
    </Box>
  );
};
